// Reception user menu — RON-5469. Prototype region: `#user-trigger` + `#dd-user`.
//
// THIS IS THE TAB'S ONLY EXIT. The reception screen opens in its own tab with no app sidebar, so
// without this there is no way back to Ronspot and no way to close the screen — which is what the
// parity audit found.
//
// Neither action needs an endpoint. Both are window plumbing, and both have a failure mode worth
// handling rather than hoping about:
//
//   `rcProfile()`  the prototype calls `window.opener.focus()`. There is no opener if the tab was
//                  restored, bookmarked, or opened by middle-click, so that alone would be a
//                  button that silently does nothing.
//   `rcClose()`    `window.close()` is refused by browsers for a tab the script did not open.
//                  A dead Close button on a desk screen is worse than none.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { getInitials } from "../receptionConstants.js";

const ReceptionUserMenu = ({ siteName, userName }) => {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [closeFailed, setCloseFailed] = useState(false);
  const wrapRef = useRef(null);

  // Click-outside and Escape. Bound only while open, so a screen that sits idle all day is not
  // running a document-level listener for a menu nobody has touched.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onDocumentClick = (event) => {
      if (!wrapRef.current?.contains(event.target)) setIsOpen(false);
    };
    const onKeyDown = (event) => event.key === "Escape" && setIsOpen(false);

    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  // Back to the Ronspot tab this one was opened from.
  const goToRonspot = () => {
    setIsOpen(false);

    try {
      if (window.opener && !window.opener.closed) {
        window.opener.focus();

        return;
      }
    } catch {
      // Cross-origin opener, or one the browser will not let us touch. Fall through.
    }

    // No opener to go back to — open the app rather than leaving the menu item inert. `noopener`
    // so the new tab cannot reach back into this one.
    window.open("/booking", "_blank", "noopener");
  };

  // Close the reception tab. `window.close()` is only honoured for a window the script opened, so
  // this checks whether anything happened instead of assuming.
  const closeTab = () => {
    // The menu deliberately stays OPEN. If the close works the tab is gone and nothing was
    // rendered anyway; if the browser refuses, the note below has somewhere to appear. Closing
    // the menu first made a refusal completely silent.
    setCloseFailed(false);

    window.close();

    // Still here a moment later means the browser refused. Say so, rather than leaving somebody
    // pressing a button that does nothing.
    setTimeout(() => {
      if (!window.closed) setCloseFailed(true);
    }, 200);
  };

  return (
    <div className="reception-usermenu" ref={wrapRef}>
      <button
        type="button"
        className="reception-usermenu__trigger"
        onClick={() => setIsOpen((current) => !current)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t("react_reception_menu")}
      >
        {/* Initials chip plus the name, as the prototype has it — the desk is shared, and whose
            session this is matters more on this screen than on any other. Falls back to the
            generic label when the name is not in storage, rather than rendering an empty circle. */}
        {userName ? (
          <>
            <span className="reception-usermenu__avatar" aria-hidden="true">
              {getInitials(userName)}
            </span>
            <span className="reception-usermenu__name">{userName}</span>
          </>
        ) : (
          t("react_reception_menu")
        )}
      </button>

      {isOpen && (
        <div className="reception-usermenu__menu" role="menu">
          <p className="reception-usermenu__head">
            {userName || t("react_reception_title")}
            {siteName ? ` · ${siteName}` : ""}
          </p>

          <button type="button" role="menuitem" className="reception-usermenu__item" onClick={goToRonspot}>
            {t("react_reception_go_to_ronspot")}
            <span aria-hidden="true">↗</span>
          </button>

          <button type="button" role="menuitem" className="reception-usermenu__item" onClick={closeTab}>
            {t("react_reception_close_tab")}
          </button>

          {/* Only after a refused close, so the note is an answer to something that just happened
              rather than a permanent instruction nobody needed. */}
          {closeFailed && (
            <p className="reception-usermenu__note" role="status">
              {t("react_reception_close_tab_manually")}
            </p>
          )}

          <p className="reception-usermenu__note">{t("react_reception_still_signed_in")}</p>
        </div>
      )}
    </div>
  );
};

export default ReceptionUserMenu;
